import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { publicApi } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";
import Seo from "@/components/seo/Seo";
import { serviceSlugFor } from "@/lib/serviceLinks";

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1546185058-592ead754d27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwc3VzdGFpbmFiaWxpdHl8ZW58MHx8fHwxNzg0NzU0MjUyfDA&ixlib=rb-4.1.0&q=85";

const FALLBACKS = {
  "manufactura-iso-9001": {
    titulo: "Implementación ISO 9001 en planta de producción",
    empresa: "Empresa del sector manufactura", sector: "Manufactura", pais: "Costa Rica", ciudad: "San José", anio: 2024,
    norma: "ISO 9001", servicio: "Implementación de sistemas de gestión", duracion: "10 meses",
    image: PLACEHOLDER_IMG,
    situacion: "Procesos sin estandarizar, alta variabilidad y ausencia de indicadores clave de proceso.",
    problema: "Reclamos frecuentes de clientes, tiempos de ciclo variables y ausencia de trazabilidad documental.",
    objetivos: ["Estandarizar procesos productivos", "Establecer indicadores clave", "Preparar la organización para certificación"],
    diagnostico: "Análisis de brechas contra requisitos ISO 9001 con cumplimiento inicial del 38%.",
    acciones: ["Levantamiento de mapa de procesos", "Diseño de procedimientos y controles", "Indicadores por proceso", "Auditoría interna", "Preparación para certificación"],
    metodologia: "Ciclo PHVA con reuniones quincenales, tableros de avance y sesiones de trabajo con los responsables.",
    desafios: "Compromiso inicial de niveles operativos y disponibilidad de tiempo.",
    soluciones: "Programas de sensibilización y coaching en sitio con los equipos.",
    resultados: "Certificación ISO 9001 obtenida en primer intento y −32% en no conformidades.",
    indicadores_antes: [{ nombre: "No conformidades/mes", valor: 18 }, { nombre: "Cumplimiento ISO 9001", valor: "38%" }],
    indicadores_despues: [{ nombre: "No conformidades/mes", valor: 12 }, { nombre: "Cumplimiento ISO 9001", valor: "96%" }],
    testimonio: "El acompañamiento fue excepcional. Aportaron rigor técnico y cercanía humana.",
    fecha: "2024-08",
  },
};

export default function CasoDetalle() {
  const { slug } = useParams();
  const [caso, setCaso] = useState(null);

  useEffect(() => {
    publicApi.casoDetail(slug)
      .then(setCaso)
      .catch(() => setCaso(FALLBACKS[slug] || { ...FALLBACKS["manufactura-iso-9001"], titulo: "Caso de éxito — próximamente" }));
  }, [slug]);

  if (!caso) return <div className="pt-32 text-center">Cargando...</div>;

  const description = caso.resultado || caso.resultados || caso.situacion;
  const relatedServiceSlug = serviceSlugFor(caso.servicio || "");
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: caso.titulo,
      description,
      image: caso.image || caso.imagen || PLACEHOLDER_IMG,
      datePublished: caso.fecha || (caso.anio ? `${caso.anio}-01-01` : undefined),
      author: { "@type": "Organization", "@id": "https://www.kbconsultoria.com/#organization", name: "KB Consultoría", url: "https://www.kbconsultoria.com/" },
      publisher: { "@type": "Organization", "@id": "https://www.kbconsultoria.com/#organization", name: "KB Consultoría", url: "https://www.kbconsultoria.com/" },
      mainEntityOfPage: `https://www.kbconsultoria.com/casos-de-exito/${slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: "https://www.kbconsultoria.com/" },
        { "@type": "ListItem", position: 2, name: "Casos de éxito", item: "https://www.kbconsultoria.com/casos-de-exito" },
        { "@type": "ListItem", position: 3, name: caso.titulo, item: `https://www.kbconsultoria.com/casos-de-exito/${slug}` },
      ],
    },
  ];

  return (
    <main className="pt-[72px]" data-testid="caso-detail-page">
      <Seo title={caso.titulo} description={description} path={`/casos-de-exito/${slug}`} image={caso.image || caso.imagen || undefined} type="article" structuredData={schema} />
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 py-8">
        <Link to="/casos-de-exito" className="inline-flex items-center gap-2 text-xs text-[#4A4A4A] hover:text-[#1A1A1A] eyebrow">
          <ArrowLeft className="w-4 h-4" /> Volver a casos
        </Link>
      </div>

      <header className="border-b border-[#1A1A1A]/10 py-8 md:py-16">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="eyebrow mb-4">{caso.sector} · {caso.norma}</div>
          <h1 className="display-1 max-w-5xl">{caso.titulo}</h1>
          <div className="mt-8 flex flex-wrap gap-6 md:gap-10 text-xs text-[#4A4A4A]">
            <Meta label="Empresa" value={caso.empresa} />
            <Meta label="Sector" value={caso.sector} />
            <Meta label="País / Ciudad" value={`${caso.pais}${caso.ciudad ? ` · ${caso.ciudad}` : ""}`} />
            <Meta label="Año" value={caso.anio} />
            <Meta label="Servicio" value={caso.servicio} />
            <Meta label="Duración" value={caso.duracion} />
          </div>
        </div>
      </header>

      {/* Hero image */}
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 py-10">
        <Reveal>
          <div className="relative aspect-[16/9] overflow-hidden">
            <img src={caso.image || caso.imagen || PLACEHOLDER_IMG} alt={caso.titulo} className="absolute inset-0 w-full h-full object-cover duotone" />
          </div>
        </Reveal>
      </div>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-8 space-y-14">
            <Block eyebrow="01" title="Situación inicial" body={caso.situacion} />
            <Block eyebrow="02" title="Problema o necesidad" body={caso.problema} />
            {caso.objetivos && (
              <div>
                <div className="eyebrow mb-3">03 · Objetivos</div>
                <ul className="space-y-2 font-serif text-xl leading-relaxed">
                  {caso.objetivos.map((o, i) => (
                    <li key={i} className="flex gap-4"><span className="text-[#1B3B2B]">·</span>{o}</li>
                  ))}
                </ul>
              </div>
            )}
            <Block eyebrow="04" title="Diagnóstico" body={caso.diagnostico} />
            {caso.acciones && (
              <div>
                <div className="eyebrow mb-3">05 · Acciones ejecutadas</div>
                <ul className="space-y-2 font-serif text-xl leading-relaxed">
                  {caso.acciones.map((o, i) => (
                    <li key={i} className="flex gap-4"><span className="text-[#1B3B2B]">·</span>{o}</li>
                  ))}
                </ul>
              </div>
            )}
            <Block eyebrow="06" title="Metodología utilizada" body={caso.metodologia} />
            <Block eyebrow="07" title="Principales desafíos" body={caso.desafios} />
            <Block eyebrow="08" title="Soluciones implementadas" body={caso.soluciones} />
            <Block eyebrow="09" title="Resultados obtenidos" body={caso.resultados} accent />

            {(caso.indicadores_antes && caso.indicadores_despues) && (
              <div>
                <div className="eyebrow mb-4">10 · Indicadores antes y después</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-6 border border-[#1A1A1A]/15">
                    <div className="eyebrow mb-3">Antes</div>
                    {caso.indicadores_antes.map((k, i) => (
                      <div key={i} className="flex justify-between py-2 border-b border-[#1A1A1A]/10 text-sm">
                        <span>{k.nombre}</span><span className="font-serif text-xl">{k.valor}</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-6 border border-[#1B3B2B]/60 bg-[#1B3B2B] text-[#F9F6F0]">
                    <div className="eyebrow text-[#F9F6F0]/60 mb-3">Después</div>
                    {caso.indicadores_despues.map((k, i) => (
                      <div key={i} className="flex justify-between py-2 border-b border-[#F9F6F0]/15 text-sm">
                        <span>{k.nombre}</span><span className="font-serif text-xl">{k.valor}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {caso.testimonio && (
              <blockquote className="border-l-2 border-[#1B3B2B] pl-6 md:pl-10 py-2">
                <p className="font-serif italic text-2xl md:text-3xl leading-tight">“{caso.testimonio}”</p>
              </blockquote>
            )}
          </div>

          <aside className="col-span-12 md:col-span-4 md:pl-6">
            <div className="sticky top-32 border border-[#1A1A1A]/15 p-8 bg-[#EFEBE2]">
              <div className="eyebrow mb-4">¿Necesita algo similar?</div>
              <p className="font-serif text-2xl leading-tight">Solicite un servicio adaptado a su organización.</p>
              <a href="/#contacto" className="mt-6 inline-flex kb-btn" data-testid="caso-cta-similar">
                Solicitar servicio similar <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              {relatedServiceSlug && <Link to={`/servicios/${relatedServiceSlug}`} className="mt-5 block text-sm underline">Conocer este servicio</Link>}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

function Block({ eyebrow, title, body, accent }) {
  if (!body) return null;
  return (
    <div>
      <div className="eyebrow mb-3">{eyebrow} · {title}</div>
      <p className={`font-serif text-2xl md:text-[1.75rem] leading-snug ${accent ? "text-[#1B3B2B]" : "text-[#1A1A1A]"}`}>{body}</p>
    </div>
  );
}

function Meta({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="eyebrow text-[#1A1A1A]/50">{label}</div>
      <div className="mt-1 text-[#1A1A1A]">{value}</div>
    </div>
  );
}
